import React, { useEffect, useState } from 'react';
import axios from '../../../api/axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getTemplateCategoriesRoute, updateTemplateDetailsRoute, createTemplateRoute, uploadTemplateImageRoute, getSiteAdminTemplateCategoriesRoute } from '../../../api/routes';
import { TOP_LEVEL_NAV_ROUTES } from '../../../config/NavRoutes';
import config from '../../../config';
import FormField from '../../../components/forms/FormField/FormField';
import TextArea from '../../../components/forms/TextArea/TextArea';
import ImageUploader, { UPLOADER_IMAGE_SIDE } from '../../../components/forms/ImageUpload/ImageUploader';
import DropDownList from '../../../components/forms/DropDownList/DropDownList';
import FormDropDown from '../../../components/forms/DropDownList/FormDropDown';
import '../SiteAdmin.scss';

const toDisplayUrl = (url) =>
    url && url.startsWith('/files/') ? `${config.apiBaseUrl}${url}` : (url || '');

const EditTemplate = ({ template, isNew = false, onBack, onSaved }) => {
    const navigate = useNavigate();
    const [name, setName] = useState(template.name || '');
    const [description, setDescription] = useState(template.description || '');
    const [templateImage, setTemplateImage] = useState(template.templateImage || '');
    const [categoryId, setCategoryId] = useState(template.categoryId?.toString() || '');
    const [templateType, setTemplateType] = useState(template.templateType || 'feature');
    const [isActive, setIsActive] = useState(template.isActive === true);
    const [isQuickstart, setIsQuickstart] = useState(!!template.isQuickstart);
    const [isFeatured, setIsFeatured] = useState(!!template.isFeatured);
    const [categories, setCategories] = useState([]);
    const [saving, setSaving] = useState(false);
    const [creatingCampaign, setCreatingCampaign] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState(template);

    const hasMultipleFeatures = currentTemplate.stepFeatureCount != null
        ? currentTemplate.stepFeatureCount > 1
        : (currentTemplate.features && currentTemplate.features.length > 1);

    useEffect(() => {
        axios.get(getSiteAdminTemplateCategoriesRoute, { withCredentials: true })
            .then(res => {
                if (res.data.categories) setCategories(res.data.categories);
            })
            .catch(() => toast.error('Failed to load categories'));
    }, []);

    const activeCategories = categories.filter(c => c.isActive === true);
    const inactiveCategories = categories.filter(c => c.isActive !== true);
    const categoryItems = [
        { label: '— Select category —', deselect: true },
        ...activeCategories.map(cat => ({ label: cat.name, value: cat.id })),
        ...(inactiveCategories.length > 0
            ? [
                { label: '— Inactive —', disabled: true, deselect: false },
                ...inactiveCategories.map(cat => ({ label: cat.name, value: cat.id })),
              ]
            : []),
    ];
    const categoryIndex = categoryId
        ? Math.max(0, categoryItems.findIndex(item => item.value === categoryId))
        : 0;

    const handleSave = async () => {
        if (!name.trim()) return toast.error('Name is required');
        if (!isNew && !templateImage.trim()) return toast.error('Image URL is required');
        if (!isNew && !categoryId) return toast.error('Category is required');

        setSaving(true);
        try {
            let res;
            if (isNew) {
                res = await axios.post(
                    createTemplateRoute,
                    { name: name.trim(), description: description.trim(), templateImage: templateImage.trim(), categoryId: categoryId || undefined, templateType },
                    { withCredentials: true }
                );
            } else {
                res = await axios.put(
                    `${updateTemplateDetailsRoute}/${currentTemplate.id}`,
                    { name: name.trim(), description: description.trim(), templateImage: templateImage.trim(), categoryId, templateType, isActive, isQuickstart, isFeatured },
                    { withCredentials: true }
                );
            }
            if (res.data.success) {
                toast.success(isNew ? 'Template created!' : 'Template saved');
                setCurrentTemplate(res.data.template);
                onSaved(res.data.template);
            }
        } catch {
            toast.error(isNew ? 'Failed to create template' : 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    const handleCreateCampaign = async () => {
        if (!currentTemplate.id) return;
        setCreatingCampaign(true);
        try {
            const res = await axios.post(
                `${updateTemplateDetailsRoute}/${currentTemplate.id}/create-campaign`,
                {},
                { withCredentials: true }
            );
            if (res.data.success) {
                toast.success('Campaign created and linked!');
                setCurrentTemplate(res.data.template);
                onSaved(res.data.template, { stayOnPage: true });
            }
        } catch {
            toast.error('Failed to create campaign');
        } finally {
            setCreatingCampaign(false);
        }
    };

    const campaignId = currentTemplate.campaignId ? currentTemplate.campaignId.toString() : null;

    return (
        <div className="edit-template-view">
            <div className="edit-template-header">
                <span className="et-back-btn" onClick={onBack} title="Back">&lt;</span>
                <h5 className="edit-template-title">
                    {isNew ? 'Create Template' : 'Edit Template'}
                    {!isNew && <span className="edit-template-subtitle">{currentTemplate.featureType}</span>}
                </h5>
            </div>

            <div className="edit-template-body">
                <div className="site-admin-panel edit-template-panel">
                    <div className="site-admin-panel-title">Template Details</div>

                    <ImageUploader
                        label="Template Image"
                        id={template.id}
                        currentImg={toDisplayUrl(templateImage)}
                        uploadImmediately={true}
                        endPoint={uploadTemplateImageRoute}
                        uploadBtnLabel="Upload Image"
                        showRemoveOption={false}
                        previewSide={UPLOADER_IMAGE_SIDE.right}
                        previewWidth={160}
                        previewHeight={90}
                        uploadedCallback={(res) => {
                            if (res.data.url) setTemplateImage(res.data.url);
                        }}
                        uploadErrorCallback={() => toast.error('Image upload failed')}
                    />

                    <FormField
                        showLabel={true}
                        label="Name"
                        type="text"
                        id="et-name"
                        placeholder="Template name"
                        value={name}
                        setFunc={setName}
                    />

                    <TextArea
                        label="Description"
                        id="et-description"
                        placeholder="Template description"
                        value={description}
                        setFunc={setDescription}
                        rows={4}
                    />

                    {categories.length > 0 && (
                        <div className="et-field">
                            <div className="et-label">Category</div>
                            <DropDownList
                                id="et-category"
                                items={categoryItems}
                                currentIndex={categoryIndex}
                                selectFunc={(e, id, item) => { if (!item.disabled) setCategoryId(item.value || ''); }}
                                enabled={true}
                                deselectedClass="drop-down-deselected"
                                selectedClass="drop-down-selected"
                                listClass=""
                            />
                        </div>
                    )}

                    {hasMultipleFeatures ? (
                        <div className="et-field">
                            <div className="et-label">Template Type</div>
                            <p className="bold-text indented">
                                Campaign Template
                                <span style={{ display: 'block', fontSize: '0.75rem', fontStyle: 'italic', fontWeight: 'normal', color: 'var(--label-color)', marginTop: '0.2rem' }}>
                                    Forced — multiple features in campaign
                                </span>
                            </p>
                        </div>
                    ) : (
                        <FormDropDown
                            label="Template Type"
                            showLabel={true}
                            id="et-template-type"
                            items={[
                                { label: 'Feature Template', value: 'feature' },
                                { label: 'Campaign Template', value: 'campaign' },
                            ]}
                            currentIndex={templateType === 'campaign' ? 1 : 0}
                            selectFunc={(e, id, item) => setTemplateType(item.value)}
                            enabled={true}
                        />
                    )}

                    <div className="et-field">
                            <div className="sa-toggle-row">
                                <span className="et-label">Active</span>
                                <div
                                    className={`tm-flag-toggle${isActive ? ' on' : ''}`}
                                    onClick={() => setIsActive(v => !v)}
                                    style={{ cursor: 'pointer' }}
                                    title={isActive ? 'Click to deactivate' : 'Click to activate'}
                                />
                            </div>
                            <p style={{ fontSize: '0.75rem', color: 'var(--label-color)', margin: '0.25rem 0 0' }}>
                                {isActive
                                    ? 'Template is visible to users'
                                    : 'Template is hidden from users'}
                            </p>
                        </div>

                    <div className="et-field">
                            <div className="sa-toggle-row">
                                <span className="et-label">Quickstart</span>
                                <div
                                    className={`tm-flag-toggle${isQuickstart ? ' on' : ''}`}
                                    onClick={() => setIsQuickstart(v => !v)}
                                    style={{ cursor: 'pointer' }}
                                    title={isQuickstart ? 'Remove from quickstart' : 'Mark as quickstart'}
                                />
                            </div>
                            <p style={{ fontSize: '0.75rem', color: 'var(--label-color)', margin: '0.25rem 0 0' }}>
                                {isQuickstart
                                    ? 'Shown in quickstart selection'
                                    : 'Not shown in quickstart selection'}
                            </p>
                        </div>

                    <div className="et-field">
                            <div className="sa-toggle-row">
                                <span className="et-label">Featured</span>
                                <div
                                    className={`tm-flag-toggle${isFeatured ? ' on' : ''}`}
                                    onClick={() => setIsFeatured(v => !v)}
                                    style={{ cursor: 'pointer' }}
                                    title={isFeatured ? 'Remove from featured' : 'Mark as featured'}
                                />
                            </div>
                            <p style={{ fontSize: '0.75rem', color: 'var(--label-color)', margin: '0.25rem 0 0' }}>
                                {isFeatured
                                    ? 'Shown in featured templates'
                                    : 'Not shown in featured templates'}
                            </p>
                        </div>

                    <div className="et-actions">
                        <div
                            className={`standard-button button-active sa-save-btn${saving ? ' button-inactive' : ''}`}
                            onClick={saving ? undefined : handleSave}
                        >
                            {saving ? 'Saving…' : isNew ? 'Create Template' : 'Save Changes'}
                        </div>
                    </div>
                </div>

                <div className="site-admin-panel edit-template-panel">
                    <div className="site-admin-panel-title">Attached Campaign</div>
                    {campaignId ? (
                        <div className="et-campaign-row">
                            <span className="et-campaign-label">
                                Campaign ID: <code className="et-campaign-id">{campaignId}</code>
                            </span>
                            <span
                                className="et-campaign-link"
                                onClick={() =>
                                    navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGN_OVERVIEW.replace(':id', campaignId))
                                }
                            >
                                View Campaign →
                            </span>
                        </div>
                    ) : currentTemplate.id ? (
                        <div>
                            <p className="et-no-campaign">No campaign attached yet.</p>
                            <div
                                className={`standard-button button-active sa-save-btn${creatingCampaign ? ' button-inactive' : ''}`}
                                onClick={creatingCampaign ? undefined : handleCreateCampaign}
                            >
                                {creatingCampaign ? 'Creating…' : 'Create Campaign'}
                            </div>
                        </div>
                    ) : (
                        <span className="et-no-campaign">Save the template first, then create a campaign.</span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditTemplate;
